import {createApi} from '@reduxjs/toolkit/query/react';
import baseQuery from 'src/redux/api/baseQuery';
import {type IregistroUsuarioResponse, type IregistroUsuarioRequest, IobtenerPersonasNaturalesResponse, IobtenerPersonasNaturalesRequest, IobtenerUsuariosRequest, IobtenerUsuariosResponse, IpersonaNaturalPorIdentificacionRequest, IpersonaNaturalMinResponse, IusuarioPorIdRequest, IusuarioPorIdResponse, IactualizarDatosPersonalesRequest, IusuarioPorUnidadRequest, IusuarioPorUnidadResponse, IusuarioPorUnidadSaveRequest, IpersonaPorIdentificacionRequest, IpersonaMinResponse, IactivarInactivarUsuarioEnUnidadRequest, IcrearAccesoUsuarioRequest, IaccesosUsuarioUnidadRequest, IdetalleAcceso, IusuarioPorPersonaRequest, IusuarioPorPersonaResponse, IobtenerParticipantesResponse, IobtenerParticipantesRequest, IusuarioSistemaResponse} from '@api/types/usuarios';
import { IeditarFinanciacionRequest, IeliminarFinanciacionRequest, IguardarFinanciacionRequest, IobtenerFinanciadoresProyectoRequest } from './types/financiadores';

/**
 * API para la gestión de usuarios
 * @see https://redux-toolkit.js.org/rtk-query/overview
 * @see https://redux-toolkit.js.org/rtk-query/usage/customizing-queries
 *
 */
export const usuariosApi = createApi({
	reducerPath: 'usuariosApi',
	baseQuery,
	tagTypes: ['usuarios', 'usuarioUnidad', 'usuariosUnidad', 'accesosUsuario', 'participantesProyecto', 'financiadoresProyecto'],
	endpoints: (builder) => ({
		obtenerUsuarios: builder.query<IusuarioSistemaResponse[], void>({
			query() {
				return {
					url: 'usuarios',
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags : ['usuarios']
		}),


		// JUPITER
		crearUsuario: builder.mutation<IregistroUsuarioResponse, IregistroUsuarioRequest>({
			query({correo, correoAlterno, ...rest}) {
				return {
					url: 'v1/usuarios',
					method: 'POST',
					credentials: 'include',
					body: {
						correo: correo.toLowerCase().trim(),
						correoAlterno: correoAlterno ? correoAlterno.toLowerCase().trim() : '',
						...rest,
					},
				};
			},
			invalidatesTags: ['usuarios', 'usuariosUnidad'],
		}),
		obtenerUsuariosPorDependencia: builder.query<IobtenerUsuariosResponse, IobtenerUsuariosRequest>({
			query({textoSearch,unidadId, paginador}) {
				return {
					url: 'v1/usuarios',
					method: 'GET',
					credentials: 'include',
					params: {
						unidadId,
						nombresLike: textoSearch,
						...paginador,
					},
				};
			},
			providesTags : (result, error, arg) => [{type: 'usuariosUnidad', id: arg.unidadId}]
		}),
		obtenerPersonasNaturales: builder.query<IobtenerPersonasNaturalesResponse, IobtenerPersonasNaturalesRequest>({
			query({textoSearch, paginador}) {
				return {
					url: 'v1/usuarios/personas-naturales',
					method: 'GET',
					credentials: 'include',
					params: {
						textoSearch,
						...paginador,
					},
				};
			},
			providesTags : ['usuarios']
		}),
		inactivarUsuario: builder.mutation<void, string>({
			query(usuarioId) {
				return {
					url: `v1/usuarios/${usuarioId}/activo`,
					method: 'PATCH',
					credentials: 'include',
				};
			},
		}),
		obtenerPersonaNaturalPorIdentificacion: builder.query<IpersonaNaturalMinResponse, IpersonaNaturalPorIdentificacionRequest>({
			query(identificacion) {
				return {
					url: `v1/usuarios/personas-naturales/${identificacion}`,
					method: 'GET',
					credentials: 'include',
				};
			},
		}),
		obtenerUsuarioPorId: builder.query<IusuarioPorIdResponse, IusuarioPorIdRequest>({
			query({usuarioId}) {
				return {
					url: `v1/usuarios/${usuarioId}`,
					method: 'GET',
					credentials: 'include',
				};
			},
		}),
		obtenerUsuarioUnidad: builder.query<IusuarioPorUnidadResponse, IusuarioPorUnidadRequest>({
			query({unidadId, usuarioId}) {
				return {
					url: `v1/usuarios/${usuarioId}/unidades/${unidadId}`,
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: (result, error, arg) => [{type: 'usuarioUnidad', id: `${arg.usuarioId}-${arg.unidadId}`}]
		}),
		actualizarDatosPersonales: builder.mutation<void, IactualizarDatosPersonalesRequest>({
			query({correo, correoAlterno, usuarioId, ...rest}) {
				return {
					url: `v1/usuarios/${usuarioId}`,
					method: 'PUT',
					credentials: 'include',
					body: {
						correo: correo.toLowerCase().trim(),
						correoAlterno: correoAlterno ? correoAlterno.toLowerCase().trim() : '',
						...rest,
					},
				};
			},
			invalidatesTags: ['usuarios']
		}),
		guardarUsuarioUnidad: builder.mutation<void, IusuarioPorUnidadSaveRequest>({
			query({unidadId, usuarioId, correoProceso}) {
				let url = `v1/usuarios/${usuarioId}/unidades/${unidadId}`;
				if (correoProceso && correoProceso.trim() !== ''){
					url = `${url}?correoProceso=${correoProceso}`;
				}
				return {
					url,
					method: 'POST',
					credentials: 'include',
					
				};
			},
			invalidatesTags: (result, error, arg) => [{type: 'usuarioUnidad', id: `${arg.usuarioId}-${arg.unidadId}`}, 'usuariosUnidad']
		}),
		obtenerPersonaPorIdentificacion: builder.query<{result: IpersonaMinResponse[]}, IpersonaPorIdentificacionRequest>({
			query({identificacion, tipoIdentificacion, tipoPersona}) {
				return {
					url: 'v1/usuarios/personas',
					method: 'GET',
					credentials: 'include',
					params: {
						identificacion,
						tipoIdentificacion,
						tipoPersona,
						page: 0,
						size: 1
					},
				};
			},
		}),
		activarInactivarUsuarioEnUnidad: builder.mutation<void, IactivarInactivarUsuarioEnUnidadRequest>({
			query({usuarioId, unidadId}) {
				return {
					url: `v1/usuarios/${usuarioId}/unidades/${unidadId}/activo`,
					method: 'POST',
					credentials: 'include',
				};
			},
			invalidatesTags: ['usuariosUnidad'],
		}),
		crearAccesoUsuario: builder.mutation<void, IcrearAccesoUsuarioRequest>({
			query({usuarioId, ...rest}) {
				return {
					url: `v1/usuarios/${usuarioId}/accesos`,
					method: 'POST',
					body: rest,
					credentials: 'include',
				};
			},
			invalidatesTags: ['accesosUsuario', 'participantesProyecto']
		}),
		obtenerAccesosPorUsuarioEnUnidad: builder.query<IdetalleAcceso[], IaccesosUsuarioUnidadRequest>({
			query({usuarioId, unidadId}) {
				return {
					url: `v1/usuarios/${usuarioId}/accesos`,
					method: 'GET',
					credentials: 'include',
					params: {
						unidadId,
					},
				};
			},
			providesTags: ['accesosUsuario']
		}),
		obtenerUsuariosVinculados: builder.query<IusuarioPorPersonaResponse[], IusuarioPorPersonaRequest>({
			query({usuarioId}) {
				return {
					url: `v1/usuarios/${usuarioId}/usuarios-unidades-disponibles`,
					method: 'GET',
					credentials: 'include',
					
				};
			},
		}),
		obtenerParticipantesDeProyecto: builder.query<IobtenerParticipantesResponse, IobtenerParticipantesRequest>({
			query({proyectoId, paginador}) {
				return {
					url: 'v1/participantes',
					method: 'GET',
					credentials: 'include',
					params: {
						proyectoId,
						...paginador,
					},
				};
			},
			providesTags: ['participantesProyecto']
		}),
		// Financiación
		obtenerFinanciadoresDeProyecto: builder.query<IobtenerParticipantesResponse, IobtenerFinanciadoresProyectoRequest>({
			query({proyectoId}) {
				return {
					url: 'v1/financiaciones',
					method: 'GET',
					credentials: 'include',
					params: {
						proyectoId
					},
				};
			},
			providesTags: ['financiadoresProyecto']
		}),
		crearFinanciacion: builder.mutation<void, IguardarFinanciacionRequest>({
			query(body) {
				return {
					url: `v1/financiaciones`,
					method: 'POST',
					body,
					credentials: 'include',
				};
			},
			invalidatesTags: ['financiadoresProyecto']
		}),
		editarFinanciacion: builder.mutation<void, IeditarFinanciacionRequest>({
			query({financiacionId,...body}) {
				return {
					url: `v1/financiaciones/${financiacionId}`,
					method: 'PUT',
					credentials: 'include',
					body,
				};
			},
			invalidatesTags: ['financiadoresProyecto'],
		}),
		eliminarFinanciacion: builder.mutation<void, IeliminarFinanciacionRequest>({
			query({financiacionId}) {
				return {
					url: `v1/financiaciones/${financiacionId}`,
					method: 'DELETE',
					credentials: 'include',
				};
			},
			invalidatesTags: ['financiadoresProyecto'],
		}),
	}),
});

export const {
	useObtenerUsuariosQuery,
	useCrearUsuarioMutation,
	useLazyObtenerPersonasNaturalesQuery,
	useObtenerPersonasNaturalesQuery,
	useLazyObtenerUsuariosPorDependenciaQuery,
	useInactivarUsuarioMutation,
	useObtenerPersonaNaturalPorIdentificacionQuery,
	useObtenerUsuarioPorIdQuery,
	useActualizarDatosPersonalesMutation,
	useObtenerUsuarioUnidadQuery,
	useGuardarUsuarioUnidadMutation,
	useLazyObtenerPersonaPorIdentificacionQuery,
	useLazyObtenerUsuarioUnidadQuery,
	useActivarInactivarUsuarioEnUnidadMutation,
	useCrearAccesoUsuarioMutation,
	useObtenerAccesosPorUsuarioEnUnidadQuery,
	useObtenerUsuariosVinculadosQuery,
	useObtenerParticipantesDeProyectoQuery,
	useObtenerFinanciadoresDeProyectoQuery,
	useCrearFinanciacionMutation,
	useEditarFinanciacionMutation,
	useEliminarFinanciacionMutation
} = usuariosApi;
